import { 
  getAuth, 
  signOut,
  onAuthStateChanged,
  signInWithCustomToken
} from 'firebase/auth';
import app from './config';
import { Result } from '../../models/result';

// Initialize Firebase Auth
const auth = getAuth(app);

class FirebaseAuthService {
  constructor() {
    this.auth = auth;
    this.currentSchoolId = null;
  }

  // Set current school context
  setSchoolContext(schoolId) {
    this.currentSchoolId = schoolId;
  }

  // Get school-specific configuration
  getSchoolConfig() {
    return this.currentSchoolId;
  }

  /**
   * Signs Firebase Auth in with the custom token returned by the ai-service login
   * (userRepo.loginWithPassword). The token carries the userId, type and schoolId claims.
   * Called by useAuthStore.signInWithCustomToken.
   * @param {string} customToken - Firebase custom token from the ai-service
   * @returns {Promise<Result>} Result with the signed-in Firebase user
   */
  async signInWithCustomToken(customToken) {
    try {
      const credential = await signInWithCustomToken(this.auth, customToken);
      return new Result({ value: credential.user });
    } catch (error) {
      const authError = this.handleAuthError(error);
      return new Result({ message: authError.message, code: authError.code });
    }
  }

  // Sign out user
  async signOut() {
    try {
      await signOut(this.auth);
      console.log('User signed out successfully');
    } catch (error) {
      console.error('Error signing out:', error);
      throw this.handleAuthError(error);
    }
  }

  // Get current user
  getCurrentUser() {
    return this.auth.currentUser;
  }

  // Listen to auth state changes
  onAuthStateChanged(callback) {
    return onAuthStateChanged(this.auth, callback);
  }

  // Handle Firebase Auth errors
  handleAuthError(error) {
    const errorMessages = {
      'auth/invalid-custom-token': 'Sign-in failed. Please try again.',
      'auth/user-disabled': 'This user account has been disabled.',
      'auth/too-many-requests': 'Too many requests. Please try again later.',
    };

    return {
      code: error.code,
      message: errorMessages[error.code] || error.message || 'An unknown error occurred.',
      originalError: error
    };
  }
}

// Export singleton instance
const firebaseAuthService = new FirebaseAuthService();
export default firebaseAuthService;

// Export auth instance for direct use if needed
export { auth };
